import React from 'react';
import {
    List,
    Datagrid,
    TextField,
    DateField,
    EditButton,
    Filter,
    TextInput,
    SelectInput,
    ReferenceInput,
    AutocompleteInput,
    TopToolbar,
    CreateButton,
    ExportButton,
    RefreshButton,
    useTranslate,
    FunctionField,
    usePermissions,
} from 'react-admin';
import { Box, Chip } from '@mui/material';
import { updateStateLabel } from './updateLabels';
import { hasPermission } from '../../utils/hasPermission';
import { useKioskAlertSummary } from '../kiosk-alerts/useKioskAlertSummary';
import { KioskDeviceAlertsChip } from '../kiosk-alerts/KioskDeviceAlertsChip';

const DEFAULT_HEARTBEAT_SECONDS = 300;

const HEALTH_STATUS_LABEL_KEYS = ['ok', 'absent', 'error', 'low', 'out', 'offline'];

const READER_STATE_STATUS: Record<string, string> = { ready: 'ok', stopped: 'absent', error: 'error', connecting: 'unknown' };

const readerStatus = (reader: unknown): string | undefined => {
    if (reader && typeof reader === 'object') {
        const state = (reader as { state?: unknown }).state;
        return typeof state === 'string' && state in READER_STATE_STATUS ? READER_STATE_STATUS[state] : 'unknown';
    }
    return typeof reader === 'string' ? reader : undefined;
};

const healthStatusColor = (status: string | undefined): 'success' | 'error' | 'warning' | 'default' => {
    if (status === 'ok') return 'success';
    if (status === 'error' || status === 'offline') return 'error';
    if (status === 'low' || status === 'out') return 'warning';
    return 'default';
};

interface HealthBadgesProps {
    record: any;
    heartbeatSeconds?: number;
}

export const HealthBadges: React.FC<HealthBadgesProps> = ({ record, heartbeatSeconds = DEFAULT_HEARTBEAT_SECONDS }) => {
    const translate = useTranslate();
    const health = record?.health || {};
    const lastHeartbeatAt = record?.lastHeartbeatAt;

    const statusLabel = (status: string | undefined): string => {
        const key = HEALTH_STATUS_LABEL_KEYS.includes(status || '') ? status : 'unknown';
        return translate(`resources.kiosk-devices.health.${key}`);
    };

    const online = lastHeartbeatAt
        ? Date.now() - new Date(lastHeartbeatAt).getTime() < heartbeatSeconds * 2 * 1000
        : false;
    const connectivityLabel = !lastHeartbeatAt
        ? translate('resources.kiosk-devices.health.neverSeen')
        : translate(online ? 'resources.kiosk-devices.health.online' : 'resources.kiosk-devices.health.offline');
    const connectivityColor = !lastHeartbeatAt ? 'default' : online ? 'success' : 'error';

    const clockSkewMs = health.clock_skew_ms;
    const clockError = typeof clockSkewMs === 'number' && Math.abs(clockSkewMs) > 60000;

    return (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            <Chip size="small" label={connectivityLabel} color={connectivityColor} />
            <Chip size="small" label={`${translate('resources.kiosk-devices.health.reader')}: ${statusLabel(readerStatus(health.reader))}`} color={healthStatusColor(readerStatus(health.reader))} />
            <Chip size="small" label={`${translate('resources.kiosk-devices.health.terminal')}: ${statusLabel(health.terminal)}`} color={healthStatusColor(health.terminal)} />
            <Chip size="small" label={`${translate('resources.kiosk-devices.health.printer')}: ${statusLabel(health.printer?.status)}`} color={healthStatusColor(health.printer?.status)} />
            <Chip size="small" label={`${translate('resources.kiosk-devices.health.paper')}: ${statusLabel(health.printer?.paper)}`} color={healthStatusColor(health.printer?.paper)} />
            <Chip size="small" label={translate('resources.kiosk-devices.health.clock')} color={clockError ? 'error' : 'success'} />
        </Box>
    );
};

const KioskDevicesFilter = (props: any) => {
    const translate = useTranslate();

    return (
        <Filter {...props}>
            <TextInput
                label={translate('ra.action.search')}
                source="q"
                alwaysOn
            />
            <ReferenceInput source="siteId" reference="sites" label={translate('resources.kiosk-devices.fields.siteId')}>
                <AutocompleteInput optionText="name" />
            </ReferenceInput>
            <SelectInput
                source="status"
                label={translate('resources.kiosk-devices.fields.status')}
                choices={[
                    { id: 'pending', name: translate('resources.kiosk-devices.status.pending') },
                    { id: 'active', name: translate('resources.kiosk-devices.status.active') },
                    { id: 'disabled', name: translate('resources.kiosk-devices.status.disabled') },
                ]}
            />
        </Filter>
    );
};

const ListActions = () => (
    <TopToolbar>
        <CreateButton />
        <ExportButton />
        <RefreshButton />
    </TopToolbar>
);

const statusChipColor = (status: string): 'warning' | 'success' | 'default' => {
    if (status === 'pending') return 'warning';
    if (status === 'active') return 'success';
    return 'default';
};

export const KioskDevicesList = () => {
    const translate = useTranslate();
    const { permissions } = usePermissions();
    const canSeeAlerts = hasPermission(permissions as string[] | null | undefined, 'kiosk-alerts:read:all');
    const alertSummary = useKioskAlertSummary(canSeeAlerts);

    return (
        <List
            filters={<KioskDevicesFilter />}
            actions={<ListActions />}
            perPage={25}
            sort={{ field: 'createdAt', order: 'DESC' }}
            sx={{
                '& .RaList-content': {
                    backgroundColor: '#FAFAFA',
                },
            }}
        >
            <Datagrid
                rowClick="edit"
                bulkActionButtons={false}
                sx={{
                    '& .RaDatagrid-headerCell': {
                        backgroundColor: '#F5F5F5',
                        fontWeight: 600,
                    },
                    '& .RaDatagrid-row': {
                        '&:hover': {
                            backgroundColor: '#F0F7FF',
                        },
                    },
                }}
            >
                <TextField source="name" label={translate('resources.kiosk-devices.fields.name')} />
                <TextField source="siteName" label={translate('resources.kiosk-devices.fields.siteName')} sortable={false} />
                <FunctionField
                    label={translate('resources.kiosk-devices.fields.status')}
                    render={(record: any) => (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            <Chip
                                size="small"
                                label={translate(`resources.kiosk-devices.status.${record.status}`)}
                                color={statusChipColor(record.status)}
                            />
                            {record.maintenanceEnabled && (
                                <Chip
                                    size="small"
                                    label={translate('resources.kiosk-devices.maintenance.chip')}
                                    color="warning"
                                />
                            )}
                        </Box>
                    )}
                />
                <FunctionField
                    label={translate('resources.kiosk-devices.fields.health')}
                    render={(record: any) => <HealthBadges record={record} />}
                />
                {canSeeAlerts && (
                    <FunctionField
                        label={translate('resources.kiosk-alerts.deviceChip.column')}
                        render={(record: any) => <KioskDeviceAlertsChip kioskId={String(record.id)} summary={alertSummary} />}
                    />
                )}
                <TextField source="appVersion" label={translate('resources.kiosk-devices.fields.appVersion')} />
                <FunctionField
                    label={translate('resources.kiosk-devices.fields.updateState')}
                    render={(record: any) => updateStateLabel(record.updateState, translate)}
                />
                <DateField source="lastHeartbeatAt" label={translate('resources.kiosk-devices.fields.lastHeartbeatAt')} showTime />
                <EditButton />
            </Datagrid>
        </List>
    );
};
